import axios from "axios";
import { toast } from "react-toastify";
import apiClient, { imageClient } from "./apiClient";
import { TRIP_STATUS } from "utils/index";

/**
 * Create Memories (Shorts/Reels)
 * @param {any} data - The data for creating memories.
 * @returns {Promise<any>} - The response data.
 **/
export const createMemories = async (data: any) => {
  try {
    const response = await apiClient.post(`/Reels`, data);
    return response.data;
  } catch (error) {
    console.error("Error creating memories:", error);
    throw error;
  }
};

/**
 * Fetch shorts (reels) by approval status
 * @param {string} tripId - The trip IDs to filter shorts by
 * @returns {Promise<any>} - Array of shorts matching the approval status
 * @throws {Error} - If there's an error during the API call
 **/

export const getShorts = async (tripId: string) => {
  try {
    const params = {
      filterParams: {
        IndexName: "tripId-index",
        KeyConditionExpression: "tripId = :tripId",
        ExpressionAttributeValues: {
          ":tripId": tripId
        }
      },
    };

    const response = await apiClient.post(`/Reels/bulk-ops/query`, params);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error("Error in memories bulk ops:", error);
    throw error;
  }
};


export const shortBulkOps = async (merchantId: string,status:string,ProjectionExpression?: string[]) => {
  try {
    const params = {
      filterParams: {
        IndexName: "merchantId-memoriesStatus-index",
        KeyConditionExpression: "merchantId = :merchantId and memoriesStatus=:status",
        ExpressionAttributeValues: {
          ":merchantId": merchantId,
          ":status": status ?? TRIP_STATUS.IN_REVIEW
        }
      },
    } as any;

    if (ProjectionExpression) {
        params.filterParams.ProjectionExpression = ProjectionExpression.join(",");
    }

    const response = await apiClient.post(`/Reels/bulk-ops/query`, params);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error("Error in memories bulk ops:", error);
    throw error;
  }
};

/**
 * @param {string} folderName - Foldername unique combination of trips(searchidkey + searchid).
 * @param {string} fileName - File name of the image to be uploaded to S3. It's also needed when fetching image.
 * @returns {Promise<any>} - The response data { uploadURL : string, filePath: string }.
 **/

export const createMemoriesUploadUrl = async (
  folderName: string,
  fileName: string
) => {
  try {
    const data = {
      folderName,
      fileName,
    };
    const response = await apiClient.post(`/s3/reels`, data);
    return response.data;
  } catch (error) {
    console.error("Error getting memories url", error);
    throw error;
  }
};

export const updateMemoriesStaus = async (id:string,status:string) => {
  try {
    const data = {
      memoriesStatus: status,
    };
    const response = await apiClient.put(`/Reels/id/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating memories status:", error);
    throw error;
  }
}

export const deleteMemories = async (data: any) => {
  try {
    const response = await apiClient.delete("/s3/reels/delete", { data });
    return response.data;
  } catch (error) {
    console.error("Error deleting memories:", error);
    throw error;
  }
};

/**
 * Update caption for a memories/reel
 * @param {string} memoriesId - The ID of the memories/reel to update
 * @param {object} data - Object containing the caption to update
 * @returns {Promise<any>} - The response data
 * @throws {Error} - If there's an error during the API call
 **/
export const updateMemoriesById = async (
  shortId: string,
  data: { caption: string }
) => {
  try {
    const response = await apiClient.put(`/Reels/id/${shortId}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating memories caption:", error);
    throw error;
  }
};

/**
 * @param {File} file - The file to be uploaded to S3.
 * @param {string} url - The URL to upload the file to.
 * @param {string} mimetype - The mimetype of the file to be uploaded to S3.
 * @returns {Promise<any>} - Status code 200 if successful.
 **/
export const uploadVideoS3 = async (
  file: File,
  url: string,
  mimetype: string
) => {
  try {
    const response = await axios.put(url, file, {
      headers: {
        "Content-Type": mimetype,
      },
    });
    return response;
  } catch (error) {
    console.error("Error uploading video", error);
    throw error;
  }
};

export const getImage = async (folderName: string, fileName: string) => {
  try {
    const response = await imageClient.get(`/${folderName}/${fileName}`);
    return response.data;
  } catch (error) {
    console.error("Error getting image", error);
    throw error;
  }
};
